import {ServiceBase} from '../ServiceBase';
import { action, observable, computed } from 'mobx';
import {
    LiveTables,
    LiveTablesArray,
    ILiveTableInfo,
    ILiveTableFeedsService,
    IFilterDetails
} from './live-table-feeds.service.interface';
import {Check, TimeSpan} from "@sparkware/app-utils";
import { CGPEventSubscription } from '../sdk/cgp-sdk.interface';
import { arraysEqual } from '../../../utils/array-helper';
import {IAiSortOptions} from "../ai-engine/ai-engine.service.interface";
import memoize from "../../../utils/memoize";

export interface IAiRecommendedLiveGames {
    engine?: string;
    gameTypes: number[];
    gameTypesMap?: Record<number, number>;
}

export interface IGetLiveGamesOptions {
    useAISortedList?: boolean;
    numberOfSortingExemptGames?: number;
}

const GAME_MAX_TIME_TO_OPEN = 3600000;

export class LiveTableFeedsService extends ServiceBase implements ILiveTableFeedsService {

    private _startTime: number;
    private _liveTableUpdateSubscription: CGPEventSubscription;

    constructor(props) {
        super(props);

        this.reaction(() => this.services.routing.currentRoute, () => {
            this._stop();
        });
    }

    @observable.ref
    private _liveCasinoRecommendedGames: IAiRecommendedLiveGames = null;

    @action.bound
    private _setLiveCasinoRecommendedGames(value: IAiRecommendedLiveGames) {

        let tempGameTypesMap = {};

        if (value.gameTypes) {
            value.gameTypes.forEach((gameType, i) => tempGameTypesMap[gameType] = i);
        }

        this._liveCasinoRecommendedGames = {...value, gameTypes: (value.gameTypes || []), gameTypesMap: tempGameTypesMap};
    }

    private _getLiveCasinoGameRecommendationsForPlayer = memoize(() => this.services.api.insightController.getLiveCasinoGameRecommendationsForPlayer({
        engineType: 1
    }));

    @observable.ref
    private _tablesData: LiveTablesArray = [];

    @observable
    public currentFilter: any = null;

    private _previousOpenTablesIds: string[] = [];
    @computed
    public get openTablesIds(): string[] {
        const currentOpenTablesIds = this._tablesData
            .filter(tableData => !Check.isEmpty(tableData.tableInfo) && tableData.tableInfo.IsOpen)
            .map(tableData => tableData.tableId);
        if (!arraysEqual(this._previousOpenTablesIds, currentOpenTablesIds)) {
            // update observer only if there is a difference between openTablesIds.
            // we don't want to cause useless reactions and re-renders
            this._previousOpenTablesIds = currentOpenTablesIds;
        }

        return this._previousOpenTablesIds;
    }

    async updateAiRecommendedLiveGames() {
        if(this.services.user.isAuthorized) {

            const aiRecommendationsData = await this._getLiveCasinoGameRecommendationsForPlayer();

            this._setLiveCasinoRecommendedGames({
                gameTypes: aiRecommendationsData.GameTypes,
                engine: aiRecommendationsData.Engine
            });
        }
    }

    @action.bound
    setCurrentFilter(currentFilter: IFilterDetails) {
        this.currentFilter = currentFilter;
    }

    get tablesData(): LiveTablesArray {

        // Check if the live feeds service needs to boot
        if (Check.isNullOrUndefined(this._liveTableUpdateSubscription)) {
            this._start();
        }

        return this._tablesData;
    }

    private _start() {
        this._startTime = this.services.time.performanceNow();
        this._liveTableUpdateSubscription = this.services.sdk.events.onLiveTableFeedsUpdate(this._onLiveFeedsUpdate.bind(this));
        this.services.sdk.startLiveTableFeeds();
    }

    private _stop() {
        if(this._liveTableUpdateSubscription) {
            this._liveTableUpdateSubscription.off();
            this._liveTableUpdateSubscription = null;
        }

        this.services.sdk.stopLiveTableFeeds();
    }

    public getTableInfo(tableId): ILiveTableInfo {
        let tableData = this.tablesData.find(tableData => tableData.tableId === tableId.toString());
        return tableData ? tableData.tableInfo : null;
    }

    private _updateOpenTimeToLocal(tableInfo: ILiveTableInfo): ILiveTableInfo {
        if(tableInfo?.IsOpen === true || Check.isNullOrUndefined(tableInfo?.OperationHoursStartTime)) {
            return tableInfo;
        }

        tableInfo.OperationHoursStartTime = this._convertTimeToLocalTimeZone(tableInfo?.OperationHoursStartTime);
        return tableInfo;
    }

    private _verifyRouletteLastNumbers(tableInfo: ILiveTableInfo): ILiveTableInfo {
        let rouletteLastNumbers = tableInfo.RouletteLast5Numbers;
        if(Check.isNullOrUndefined(rouletteLastNumbers)) {
            return tableInfo;
        }

        let rouletteLast5NumbersConverted = [];
        if(rouletteLastNumbers && rouletteLastNumbers.length === 0) {
            rouletteLast5NumbersConverted = rouletteLastNumbers;
        } else {
            // all the cells in the array should be number.
            rouletteLast5NumbersConverted = rouletteLastNumbers.map(cell=> {
                // Should not start with 0 and should be number
                if(cell?.startsWith('0') && cell.length > 1 && cell !== '00') {
                    return null;
                } else {
                    if(cell !== '00') {
                        return isNaN(Number(cell))? null : cell;
                    }

                    return cell;
                }
            });

            rouletteLast5NumbersConverted = rouletteLast5NumbersConverted.filter(cell=> !Check.isNullOrUndefined(cell));

            // If not all cells are numbers should remove the last roulette numbers property.
            if(rouletteLast5NumbersConverted.length > 0) {
                // Take only last 5 numbers(defense if we got more)
                rouletteLast5NumbersConverted = rouletteLast5NumbersConverted.slice(0,5);
            } else {
                rouletteLast5NumbersConverted = [];
            }
        }
        tableInfo.RouletteLast5Numbers = rouletteLast5NumbersConverted;
        return tableInfo;
    }

    @action.bound
    private _onLiveFeedsUpdate(data: LiveTables): void {
        // we shallow clone the data because SDK always sends the same instance
        this._tablesData = Object.keys(data).map(tableId => ({
            tableId,
            tableInfo: {...this._manipulateFeedData(data[tableId])}
        }));

        if(!Check.isNullOrUndefined(this._startTime)) {
            this._sendEvent();
            this._startTime = null;
        }
    }

    private _manipulateFeedData(tableInfo: ILiveTableInfo): ILiveTableInfo {
        if(!Check.isNullOrUndefined(tableInfo)) {
            tableInfo = this._updateOpenTimeToLocal(tableInfo);
            tableInfo = this._verifyRouletteLastNumbers(tableInfo);
        }

        return tableInfo;
    }

    private _sendEvent(): void {
        let evoLiveFeedResponseTime = this.services.splash.computeSplashTimeInSeconds(this._startTime);
        this.services.analytics.sendTechnicalEvent('LIVE_TABLES_CALL', 'LIVE_TABLES_RESPONSE_TIME', evoLiveFeedResponseTime);
    }

    private _convertTimeToLocalTimeZone(timeUTC: string): string {
        return TimeSpan.parse(timeUTC).toLocalTime().toHoursAndMinutesString();
    }

    public getLiveGames(gamesIds: number[], options?: IGetLiveGamesOptions): number[] {
        let filteredGameIds = gamesIds.filter((gameID) => {
            const gameInfo = this.services.gamesFilter.filteredGamesMap[gameID];
            return (
                gameInfo && (
                    !Check.isEmpty(this.getTableInfo(gameID)) ||(
                        !Check.isEmpty(gameInfo?.gameArenaIcon) &&  !Check.isEmpty(this.tablesData)
                    )
                )
            );
        });

        if (this._shouldSortGames(options?.useAISortedList)) {
            const sortingOptions: IAiSortOptions = {
                gamesMapOverride: this._liveCasinoRecommendedGames?.gameTypesMap,
                ignoreNumberOfGames: Check.isNumber(options?.numberOfSortingExemptGames) ? options?.numberOfSortingExemptGames : 0

            };
            return this.services.aiEngine.sortGames(filteredGameIds, sortingOptions);
        }
        return filteredGameIds;
    }

    private _shouldSortGames(useAISortedList: boolean = false): boolean {
        return (useAISortedList || this.services.abTestingAiSortGamesLists.arenaHasAiSorting("newLiveCasino"))
            && !Check.isEmpty(this._liveCasinoRecommendedGames?.gameTypes);
    }

    private _isGameOpenSoon(startTime: string): boolean {
        let currentDate = this.services.time.getCurrentDate();
        let timeToOpen = TimeSpan.parse(startTime).totalMilliseconds - TimeSpan.parse(currentDate.getHours() + ':' + currentDate.getMinutes()).totalMilliseconds;
        return timeToOpen <= GAME_MAX_TIME_TO_OPEN && timeToOpen > 0;
    }

    moveGamesThatOpenSoonToTheEnd(gamesID: number[]): number[] {
        let closeGamesArr: number[] = [];
        for (let index = 0; index < gamesID.length; index++) {
            let currentGame = gamesID[index];
            let tableInfo = this.getTableInfo(currentGame);
            if (!Check.isEmpty(tableInfo) && !tableInfo.IsOpen) {
                delete gamesID[index];

                // Not show games that not open soon.
                if (tableInfo.OperationHoursStartTime) {
                    if (this._isGameOpenSoon(tableInfo.OperationHoursStartTime)) {
                        closeGamesArr.push(currentGame);
                    }
                }
            }
        }

        gamesID = gamesID.filter(game => !Check.isNullOrUndefined(game));
        return gamesID.concat(closeGamesArr);
    }

}
